import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { getErrorDescription, getErrorCategory, getErrorCategoryColor, getErrorCategoryIcon } from '../utils/iyzicoErrorCodes';

function AdminPaymentTransactions() {
    const { token } = useAuth();
    const [transactions, setTransactions] = useState([]);
    const [statistics, setStatistics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedTransaction, setSelectedTransaction] = useState(null);
    const [showModal, setShowModal] = useState(false);
    
    // Filtreler
    const [filters, setFilters] = useState({
        status: '',
        startDate: '',
        endDate: '',
        page: 1,
        limit: 20
    });

    useEffect(() => {
        fetchTransactions();
    }, [filters]);

    const fetchTransactions = async () => {
        try {
            setLoading(true);
            setError('');
            
            const params = new URLSearchParams();
            if (filters.status) params.append('status', filters.status);
            if (filters.startDate) params.append('startDate', filters.startDate);
            if (filters.endDate) params.append('endDate', filters.endDate);
            params.append('page', filters.page);
            params.append('limit', filters.limit);

            const response = await api.get(`/api/payment/admin/transactions?${params.toString()}`);
            
            // API response: { success, transactions, statistics }
            const data = response.data || response;
            setTransactions(data.transactions || []);
            setStatistics(data.statistics || null);
        } catch (err) {
            setError('Transactions yüklenirken hata oluştu: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchTransactionDetail = async (id) => {
        try {
            const response = await api.get(`/api/payment/admin/transactions/${id}`);
            const data = response.data || response;
            setSelectedTransaction(data.transaction);
            setShowModal(true);
        } catch (err) {
            alert('Transaction detayı alınamadı: ' + err.message);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            success: 'bg-green-100 text-green-800',
            failed: 'bg-red-100 text-red-800',
            pending: 'bg-yellow-100 text-yellow-800',
            processing: 'bg-blue-100 text-blue-800'
        };
        return badges[status] || 'bg-gray-100 text-gray-800';
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleString('tr-TR');
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY'
        }).format(amount);
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <h1 className="text-3xl font-bold mb-6">Payment Transactions</h1>

            {/* İstatistikler */}
            {statistics && (
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
                    <div className="bg-white p-4 rounded-lg shadow">
                        <div className="text-sm text-gray-500">Toplam</div>
                        <div className="text-2xl font-bold">{statistics.total}</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded-lg shadow">
                        <div className="text-sm text-green-600">Başarılı</div>
                        <div className="text-2xl font-bold text-green-700">{statistics.success}</div>
                    </div>
                    <div className="bg-red-50 p-4 rounded-lg shadow">
                        <div className="text-sm text-red-600">Başarısız</div>
                        <div className="text-2xl font-bold text-red-700">{statistics.failed}</div>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded-lg shadow">
                        <div className="text-sm text-yellow-600">Beklemede</div>
                        <div className="text-2xl font-bold text-yellow-700">{statistics.pending}</div>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg shadow">
                        <div className="text-sm text-blue-600">Başarı Oranı</div>
                        <div className="text-2xl font-bold text-blue-700">{statistics.successRate}%</div>
                    </div>
                </div>
            )}

            {/* Filtreler */}
            <div className="bg-white p-4 rounded-lg shadow mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Durum
                        </label>
                        <select
                            value={filters.status}
                            onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                        >
                            <option value="">Tümü</option>
                            <option value="success">Başarılı</option>
                            <option value="failed">Başarısız</option>
                            <option value="pending">Beklemede</option>
                            <option value="processing">İşleniyor</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Başlangıç Tarihi
                        </label>
                        <input
                            type="date"
                            value={filters.startDate}
                            onChange={(e) => setFilters({ ...filters, startDate: e.target.value, page: 1 })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Bitiş Tarihi
                        </label>
                        <input
                            type="date"
                            value={filters.endDate}
                            onChange={(e) => setFilters({ ...filters, endDate: e.target.value, page: 1 })}
                            className="w-full border border-gray-300 rounded-md px-3 py-2"
                        />
                    </div>
                    <div className="flex items-end">
                        <button
                            onClick={() => setFilters({ status: '', startDate: '', endDate: '', page: 1, limit: 20 })}
                            className="w-full bg-gray-200 hover:bg-gray-300 text-gray-700 py-2 px-4 rounded-md"
                        >
                            Filtreleri Temizle
                        </button>
                    </div>
                </div>
            </div>

            {error && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                </div>
            )}

            {/* Transactions Tablosu */}
            {loading ? (
                <div className="text-center py-12">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
                    <p className="mt-2 text-gray-600">Yükleniyor...</p>
                </div>
            ) : (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Tarih
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Sipariş No
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Kullanıcı
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Tutar
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Durum
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        İşlem
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {transactions.map((tx) => (
                                    <tr key={tx.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatDate(tx.created_at)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {tx.orders?.order_number || '-'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            <div>{tx.users?.full_name || 'N/A'}</div>
                                            <div className="text-xs text-gray-500">{tx.users?.email}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {formatCurrency(tx.amount || 0)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadge(tx.status)}`}>
                                                {tx.status}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <button
                                                onClick={() => fetchTransactionDetail(tx.id)}
                                                className="text-blue-600 hover:text-blue-900"
                                            >
                                                Detay
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {transactions.length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                            Transaction bulunamadı
                        </div>
                    )}
                </div>
            )}

            {/* Transaction Detay Modal */}
            {showModal && selectedTransaction && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex justify-between items-start mb-4">
                                <h2 className="text-2xl font-bold">Transaction Detay</h2>
                                <button
                                    onClick={() => setShowModal(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <span className="text-2xl">&times;</span>
                                </button>
                            </div>

                            <div className="space-y-4">
                                {/* Önemli Bilgiler - Vurgulu */}
                                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        {/* Sipariş Bilgisi */}
                                        {selectedTransaction.orders && (
                                            <div className="col-span-2 border-b pb-3">
                                                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">📦 Sipariş Bilgisi</label>
                                                <p className="text-lg font-bold text-gray-900">
                                                    {selectedTransaction.orders.order_number}
                                                </p>
                                                {selectedTransaction.orders.total_price && (
                                                    <p className="text-sm text-gray-600 mt-1">
                                                        Sipariş Tutarı: {formatCurrency(selectedTransaction.orders.total_price)}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                        
                                        {/* Kullanıcı Bilgisi */}
                                        {selectedTransaction.users && (
                                            <div className="col-span-2 border-b pb-3">
                                                <label className="block text-xs font-medium text-gray-500 uppercase mb-1">👤 Müşteri Bilgisi</label>
                                                <p className="text-base font-semibold text-gray-900">
                                                    {selectedTransaction.users.full_name || 'N/A'}
                                                </p>
                                                <p className="text-sm text-gray-600">{selectedTransaction.users.email}</p>
                                                {selectedTransaction.users.phone && (
                                                    <p className="text-sm text-gray-600">{selectedTransaction.users.phone}</p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="grid grid-cols-2 gap-4 mt-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 uppercase">Transaction ID</label>
                                            <p className="mt-1 text-sm text-gray-900 font-mono break-all">{selectedTransaction.id}</p>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 uppercase">Durum</label>
                                            <span className={`mt-1 inline-block px-3 py-1 text-sm font-semibold rounded-full ${getStatusBadge(selectedTransaction.status)}`}>
                                                {selectedTransaction.status}
                                            </span>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 uppercase">Tutar</label>
                                            <p className="mt-1 text-base font-bold text-gray-900">{formatCurrency(selectedTransaction.amount || 0)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-500 uppercase">Tarih</label>
                                            <p className="mt-1 text-sm text-gray-900">{formatDate(selectedTransaction.created_at)}</p>
                                        </div>
                                        {selectedTransaction.iyzico_payment_id && (
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 uppercase">İyzico Payment ID</label>
                                                <p className="mt-1 text-sm text-gray-900 font-mono">{selectedTransaction.iyzico_payment_id}</p>
                                            </div>
                                        )}
                                        {selectedTransaction.card_family && (
                                            <div>
                                                <label className="block text-xs font-medium text-gray-500 uppercase">Kart Bilgisi</label>
                                                <p className="mt-1 text-sm text-gray-900">
                                                    {selectedTransaction.card_family} - **** {selectedTransaction.last_four_digits}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Request Payload */}
                                {selectedTransaction.request_payload && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">İyzico İstek (Request)</label>
                                        <div className="bg-gray-50 p-4 rounded">
                                            <pre className="text-xs overflow-x-auto">
                                                {JSON.stringify(selectedTransaction.request_payload, null, 2)}
                                            </pre>
                                        </div>
                                    </div>
                                )}

                                {/* Response Payload */}
                                {selectedTransaction.response_payload && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            İyzico Yanıt (Response)
                                            {selectedTransaction.status === 'success' && <span className="ml-2 text-green-600">✓ Başarılı</span>}
                                            {selectedTransaction.status === 'failed' && <span className="ml-2 text-red-600">✗ Başarısız</span>}
                                        </label>
                                        <div className={`p-4 rounded ${
                                            selectedTransaction.status === 'success' ? 'bg-green-50' : 
                                            selectedTransaction.status === 'failed' ? 'bg-red-50' : 'bg-gray-50'
                                        }`}>
                                            {/* Önemli bilgileri vurgula */}
                                            {selectedTransaction.status === 'success' && selectedTransaction.response_payload && (
                                                <div className="mb-4 space-y-2 border-b pb-4">
                                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                                        <div>
                                                            <span className="font-semibold text-gray-700">Payment ID:</span>
                                                            <span className="ml-2">{selectedTransaction.response_payload.paymentId}</span>
                                                        </div>
                                                        <div>
                                                            <span className="font-semibold text-gray-700">Ödenen Tutar:</span>
                                                            <span className="ml-2">{selectedTransaction.response_payload.paidPrice} {selectedTransaction.response_payload.currency}</span>
                                                        </div>
                                                        <div>
                                                            <span className="font-semibold text-gray-700">Kart Türü:</span>
                                                            <span className="ml-2">{selectedTransaction.response_payload.cardFamily} - {selectedTransaction.response_payload.cardType}</span>
                                                        </div>
                                                        <div>
                                                            <span className="font-semibold text-gray-700">Kart Son 4 Hane:</span>
                                                            <span className="ml-2">**** {selectedTransaction.response_payload.lastFourDigits}</span>
                                                        </div>
                                                        <div>
                                                            <span className="font-semibold text-gray-700">Taksit:</span>
                                                            <span className="ml-2">{selectedTransaction.response_payload.installment} ay</span>
                                                        </div>
                                                        <div>
                                                            <span className="font-semibold text-gray-700">İyzico Komisyon:</span>
                                                            <span className="ml-2">{selectedTransaction.response_payload.iyziCommissionRateAmount} {selectedTransaction.response_payload.currency}</span>
                                                        </div>
                                                        <div>
                                                            <span className="font-semibold text-gray-700">Net Tutar:</span>
                                                            <span className="ml-2 font-bold text-green-600">
                                                                {selectedTransaction.response_payload.itemTransactions?.[0]?.merchantPayoutAmount || 0} {selectedTransaction.response_payload.currency}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            <details className="cursor-pointer">
                                                <summary className="text-sm font-medium text-gray-700 hover:text-gray-900 mb-2">Ham JSON Verisi</summary>
                                                <pre className="text-xs overflow-x-auto mt-2">
                                                    {JSON.stringify(selectedTransaction.response_payload, null, 2)}
                                                </pre>
                                            </details>
                                        </div>
                                    </div>
                                )}

                                {/* Backward compatibility - Old payload field */}
                                {selectedTransaction.payload && !selectedTransaction.request_payload && !selectedTransaction.response_payload && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            İyzico Yanıt (Response)
                                            {selectedTransaction.status === 'success' && <span className="ml-2 text-green-600">✓ Başarılı</span>}
                                            {selectedTransaction.status === 'failed' && <span className="ml-2 text-red-600">✗ Başarısız</span>}
                                        </label>
                                        <div className={`p-4 rounded ${
                                            selectedTransaction.status === 'success' ? 'bg-green-50' : 
                                            selectedTransaction.status === 'failed' ? 'bg-red-50' : 'bg-gray-50'
                                        }`}>
                                            {/* Önemli bilgileri vurgula (eski format için) */}
                                            {selectedTransaction.status === 'success' && selectedTransaction.payload && (
                                                <div className="mb-4 space-y-2 border-b pb-4">
                                                    <div className="grid grid-cols-2 gap-2 text-sm">
                                                        {selectedTransaction.payload.paymentId && (
                                                            <div>
                                                                <span className="font-semibold text-gray-700">Payment ID:</span>
                                                                <span className="ml-2">{selectedTransaction.payload.paymentId}</span>
                                                            </div>
                                                        )}
                                                        {selectedTransaction.payload.paidPrice && (
                                                            <div>
                                                                <span className="font-semibold text-gray-700">Ödenen Tutar:</span>
                                                                <span className="ml-2">{selectedTransaction.payload.paidPrice} {selectedTransaction.payload.currency || 'TRY'}</span>
                                                            </div>
                                                        )}
                                                        {selectedTransaction.payload.cardFamily && (
                                                            <div>
                                                                <span className="font-semibold text-gray-700">Kart Türü:</span>
                                                                <span className="ml-2">{selectedTransaction.payload.cardFamily} - {selectedTransaction.payload.cardType}</span>
                                                            </div>
                                                        )}
                                                        {selectedTransaction.payload.lastFourDigits && (
                                                            <div>
                                                                <span className="font-semibold text-gray-700">Kart Son 4 Hane:</span>
                                                                <span className="ml-2">**** {selectedTransaction.payload.lastFourDigits}</span>
                                                            </div>
                                                        )}
                                                        {selectedTransaction.payload.installment && (
                                                            <div>
                                                                <span className="font-semibold text-gray-700">Taksit:</span>
                                                                <span className="ml-2">{selectedTransaction.payload.installment} ay</span>
                                                            </div>
                                                        )}
                                                        {selectedTransaction.payload.iyziCommissionRateAmount && (
                                                            <div>
                                                                <span className="font-semibold text-gray-700">İyzico Komisyon:</span>
                                                                <span className="ml-2">{selectedTransaction.payload.iyziCommissionRateAmount} {selectedTransaction.payload.currency || 'TRY'}</span>
                                                            </div>
                                                        )}
                                                        {selectedTransaction.payload.itemTransactions?.[0]?.merchantPayoutAmount && (
                                                            <div>
                                                                <span className="font-semibold text-gray-700">Net Tutar:</span>
                                                                <span className="ml-2 font-bold text-green-600">
                                                                    {selectedTransaction.payload.itemTransactions[0].merchantPayoutAmount} {selectedTransaction.payload.currency || 'TRY'}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                            <details className="cursor-pointer">
                                                <summary className="text-sm font-medium text-gray-700 hover:text-gray-900 mb-2">Ham JSON Verisi</summary>
                                                <pre className="text-xs overflow-x-auto mt-2">
                                                    {JSON.stringify(selectedTransaction.payload, null, 2)}
                                                </pre>
                                            </details>
                                        </div>
                                    </div>
                                )}

                                {selectedTransaction.error_message && (
                                    <div className="bg-red-50 border-2 border-red-200 p-6 rounded-lg">
                                        <div className="flex items-start space-x-3">
                                            <div className="flex-shrink-0 text-3xl">
                                                {selectedTransaction.error_code ? 
                                                    getErrorCategoryIcon(getErrorCategory(selectedTransaction.error_code)) : 
                                                    '❌'
                                                }
                                            </div>
                                            <div className="flex-1">
                                                <label className="block text-lg font-bold text-red-800 mb-3">Ödeme Hatası</label>
                                                
                                                {/* Hata Kodu ve Kategorisi */}
                                                {selectedTransaction.error_code && (
                                                    <div className="mb-4">
                                                        <div className="flex items-center gap-2 mb-2">
                                                            <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                                                                getErrorCategoryColor(getErrorCategory(selectedTransaction.error_code))
                                                            }`}>
                                                                Hata Kodu: {selectedTransaction.error_code}
                                                            </span>
                                                        </div>
                                                        <div className="bg-white p-4 rounded border border-red-300">
                                                            <p className="text-base font-semibold text-gray-800 mb-2">
                                                                {getErrorDescription(selectedTransaction.error_code)}
                                                            </p>
                                                            <p className="text-sm text-gray-600">
                                                                Teknik Mesaj: {selectedTransaction.error_message}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}
                                                
                                                {/* Hata kodu yoksa sadece mesaj */}
                                                {!selectedTransaction.error_code && (
                                                    <div className="bg-white p-4 rounded border border-red-300">
                                                        <p className="text-sm text-red-700">{selectedTransaction.error_message}</p>
                                                    </div>
                                                )}
                                                
                                                {/* Öneriler */}
                                                {selectedTransaction.error_code && (
                                                    <div className="mt-4 bg-yellow-50 border border-yellow-300 p-3 rounded">
                                                        <p className="text-sm font-semibold text-yellow-800 mb-1">💡 Çözüm Önerileri:</p>
                                                        <ul className="text-sm text-yellow-700 list-disc list-inside space-y-1">
                                                            {getErrorCategory(selectedTransaction.error_code) === 'insufficient_funds' && (
                                                                <li>Müşterinin kartında yeterli bakiye olmayabilir</li>
                                                            )}
                                                            {getErrorCategory(selectedTransaction.error_code) === 'invalid_pin_cvv' && (
                                                                <>
                                                                    <li>CVV kodu veya şifre yanlış girilmiş olabilir</li>
                                                                    <li>Müşteriden bilgileri kontrol etmesini isteyin</li>
                                                                </>
                                                            )}
                                                            {getErrorCategory(selectedTransaction.error_code) === 'expired' && (
                                                                <li>Kartın son kullanma tarihi geçmiş</li>
                                                            )}
                                                            {getErrorCategory(selectedTransaction.error_code) === 'fraud' && (
                                                                <>
                                                                    <li>Güvenlik nedeniyle işlem engellenmiş</li>
                                                                    <li>Müşteri bankasını aramalı</li>
                                                                </>
                                                            )}
                                                            {getErrorCategory(selectedTransaction.error_code) === 'api_error' && (
                                                                <>
                                                                    <li>Sistem yapılandırması kontrol edilmeli</li>
                                                                    <li>API bilgileri doğru mu kontrol edin</li>
                                                                </>
                                                            )}
                                                            {getErrorCategory(selectedTransaction.error_code) === 'declined' && (
                                                                <>
                                                                    <li>Banka işlemi onaylamadı</li>
                                                                    <li>Müşteri farklı bir kart deneyebilir</li>
                                                                </>
                                                            )}
                                                            <li>Müşteri başka bir ödeme yöntemi deneyebilir</li>
                                                        </ul>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminPaymentTransactions;
